import React, { useEffect, useContext, Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import Meta from './components/Meta';
import Schema from './components/Schema';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Header from './components/Header';
import FooterWrapper from './components/FooterWrapper';
import Loader from './components/Loader';

import { useDispatch, useSelector } from 'react-redux';
import { fetchChats } from './features/chat/chatSlice';
import { SocketContext } from './context/SocketContext';


const HomeScreen = lazy(() => import('./screens/HomeScreen'));
const ProductScreen = lazy(() => import('./screens/ProductScreen'));
const LoginScreen = lazy(() => import('./screens/LoginScreen'));
const RegisterScreen = lazy(() => import('./screens/RegisterScreen'));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen'));
const FeedScreen = lazy(() => import('./screens/FeedScreen'));
const SellerScreen = lazy(() => import('./screens/SellerScreen'));
const ProductListScreen = lazy(() => import('./screens/ProductListScreen'));
const ProductEditScreen = lazy(() => import('./screens/ProductEditScreen'));
const UserListScreen = lazy(() => import('./screens/UserListScreen'));
const UserEditScreen = lazy(() => import('./screens/UserEditScreen'));
const AdminProductListScreen = lazy(() => import('./screens/AdminProductListScreen'));
const BlogListScreen = lazy(() => import('./screens/BlogListScreen'));
const BlogPostScreen = lazy(() => import('./screens/BlogPostScreen'));
const AdminPostListScreen = lazy(() => import('./screens/AdminPostListScreen'));
const PostEditScreen = lazy(() => import('./screens/PostEditScreen'));
const MessageCenterScreen = lazy(() => import('./screens/MessageCenterScreen'));
const BannerListScreen = lazy(() => import('./screens/BannerListScreen'));
const ForgotPasswordScreen = lazy(() => import('./screens/ForgotPasswordScreen'));
const ProfileCompletionScreen = lazy(() => import('./screens/ProfileCompletionScreen'));
const UserSearchScreen = lazy(() => import('./screens/UserSearchScreen'));
const WishlistScreen = lazy(() => import('./screens/WishlistScreen'));
const LocationScreen = lazy(() => import('./screens/LocationScreen'));
const LocationDetailScreen = lazy(() => import('./screens/LocationDetailScreen'));
const MySpotsScreen = lazy(() => import('./screens/MySpotsScreen'));
const FounderScreen = lazy(() => import('./screens/FounderScreen'));
const PrivacyPolicyScreen = lazy(() => import('./screens/PrivacyPolicyScreen'));
const TermsScreen = lazy(() => import('./screens/TermsScreen'));
const AboutScreen = lazy(() => import('./screens/AboutScreen'));
const NotFoundScreen = lazy(() => import('./screens/NotFoundScreen'));



const App = () => {
    const dispatch = useDispatch();
    const { userInfo } = useSelector((state) => state.user);
    const { socket } = useContext(SocketContext);

    useEffect(() => {
        if (userInfo && socket) {
            socket.emit("setup", userInfo);
            socket.on("message_received", () => {
                dispatch(fetchChats());
            });
        }
    }, [userInfo, dispatch, socket]);

    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!googleClientId) {
        console.error("Google Client ID is missing in environment variables!");
    }

    return (
        <GoogleOAuthProvider clientId={googleClientId || "missing-client-id"}>
            <HelmetProvider>
                <Meta />
                <Schema />
                <Router>
                    <Header />
                    <main className="py-3">
                        <Container fluid className="px-4 px-lg-5">
                            <Suspense fallback={<Loader />}>
                                <Routes>
                                    <Route path="/product/:id" element={<ProductScreen />} />
                                    <Route path="/shop" element={<HomeScreen />} />
                                    <Route path="/seller/:id" element={<SellerScreen />} />
                                    <Route path="/login" element={<LoginScreen />} />
                                    <Route path="/register" element={<RegisterScreen />} />
                                    <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
                                    <Route path="/complete-profile" element={<ProfileCompletionScreen />} />
                                    <Route path="/reset-password/:token" element={<LoginScreen />} />

                                    <Route path="/profile" element={<ProfileScreen />} />
                                    <Route path="/profile/:id" element={<ProfileScreen />} />
                                    <Route path="/feed" element={<FeedScreen />} />
                                    <Route path="/seller/productlist" element={<ProductListScreen />} />
                                    <Route path="/seller/product/create" element={<ProductEditScreen />} />
                                    <Route path="/seller/product/:id/edit" element={<ProductEditScreen />} />
                                    <Route path="/admin/userlist" element={<UserListScreen />} />
                                    <Route path="/admin/user/:id/edit" element={<UserEditScreen />} />
                                    <Route path="/admin/productlist" element={<AdminProductListScreen />} />
                                    <Route path='/blog' element={<BlogListScreen />} />
                                    <Route path='/blog/:id' element={<BlogPostScreen />} />
                                    <Route path='/admin/postlist' element={<AdminPostListScreen />} />
                                    <Route path='/admin/post/:id/edit' element={<PostEditScreen />} />
                                    <Route path='/admin/bannerlist' element={<BannerListScreen />} />

                                    <Route path='/search/:keyword' element={<HomeScreen />} />
                                    <Route path='/search/user/:keyword' element={<UserSearchScreen />} />
                                    <Route path='/message-center' element={<MessageCenterScreen />} />
                                    <Route path='/wishlist' element={<WishlistScreen />} />
                                    <Route path='/location' element={<LocationScreen />} />
                                    <Route path='/location/:id' element={<LocationDetailScreen />} />
                                    <Route path='/myspots' element={<MySpotsScreen />} />
                                    <Route path='/founder' element={<FounderScreen />} />
                                    <Route path='/privacy' element={<PrivacyPolicyScreen />} />
                                    <Route path='/terms' element={<TermsScreen />} />
                                    <Route path='/about' element={<AboutScreen />} />
                                    <Route path="/" element={<HomeScreen />} exact />
                                    <Route path="*" element={<NotFoundScreen />} />
                                </Routes>
                            </Suspense>
                        </Container>
                    </main>

                    <FooterWrapper />
                </Router>
            </HelmetProvider>
        </GoogleOAuthProvider>
    );
};

export default App;